import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthHeader } from '../../../utils/auth';
import { Plus, Edit2, Trash2, Users, MapPin } from 'lucide-react';
import '../../../styles/admin/production/Teams.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Teams = () => {
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    leader_employee_id: '',
    employee_ids: [],
    home_zone: '',
    service_capabilities: [],
    notes: ''
  });
  const [newCapability, setNewCapability] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamsRes, employeesRes] = await Promise.all([
        axios.get(`${API_URL}/api/teams`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/employees`, { headers: getAuthHeader() })
      ]);
      setTeams(teamsRes.data);
      setEmployees(employeesRes.data.filter(e => e.status === 'active'));
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const handleOpenModal = (team = null) => {
    if (team) {
      setEditingTeam(team);
      setFormData({
        name: team.name,
        code: team.code,
        leader_employee_id: team.leader_employee_id || '',
        employee_ids: team.employee_ids || [],
        home_zone: team.home_zone || '',
        service_capabilities: team.service_capabilities || [],
        notes: team.notes || ''
      });
    } else {
      setEditingTeam(null);
      setFormData({
        name: '',
        code: '',
        leader_employee_id: '',
        employee_ids: [],
        home_zone: '',
        service_capabilities: [],
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingTeam) {
        await axios.put(
          `${API_URL}/api/teams/${editingTeam.id}`,
          formData,
          { headers: getAuthHeader() }
        );
      } else {
        await axios.post(
          `${API_URL}/api/teams`,
          formData,
          { headers: getAuthHeader() }
        );
      }
      
      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving team:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (teamId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette équipe ?')) return;
    
    try {
      await axios.delete(
        `${API_URL}/api/teams/${teamId}`,
        { headers: getAuthHeader() }
      );
      fetchData();
    } catch (error) {
      console.error('Error deleting team:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleAddCapability = () => {
    if (newCapability.trim() && !formData.service_capabilities.includes(newCapability.trim())) {
      setFormData({
        ...formData,
        service_capabilities: [...formData.service_capabilities, newCapability.trim()]
      });
      setNewCapability('');
    }
  };

  const handleRemoveCapability = (capability) => {
    setFormData({
      ...formData,
      service_capabilities: formData.service_capabilities.filter(c => c !== capability)
    });
  };

  const handleToggleEmployee = (employeeId) => {
    const isSelected = formData.employee_ids.includes(employeeId);
    setFormData({
      ...formData,
      employee_ids: isSelected
        ? formData.employee_ids.filter(id => id !== employeeId)
        : [...formData.employee_ids, employeeId]
    });
  };

  const getEmployeeName = (employeeId) => {
    const emp = employees.find(e => e.id === employeeId);
    return emp ? emp.full_name : 'Employé inconnu';
  };

  if (loading) {
    return <div className="teams-loading">Chargement...</div>;
  }

  return (
    <div className="teams-container">
      <div className="teams-header">
        <div>
          <h2>Gestion des Équipes</h2>
          <p>{teams.length} équipe(s)</p>
        </div>
        <button onClick={() => handleOpenModal()} className="btn-add">
          <Plus size={20} />
          Nouvelle équipe
        </button>
      </div>

      <div className="teams-grid">
        {teams.map(team => (
          <div key={team.id} className="team-card">
            <div className="team-card-header">
              <div>
                <h3>{team.name}</h3>
                <span className="team-code">{team.code}</span>
              </div>
              <div className="team-actions">
                <button onClick={() => handleOpenModal(team)} className="btn-icon">
                  <Edit2 size={16} />
                </button>
                <button onClick={() => handleDelete(team.id)} className="btn-icon btn-danger">
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="team-card-body">
              {team.home_zone && (
                <div className="team-info">
                  <MapPin size={14} />
                  <span>{team.home_zone}</span>
                </div>
              )}
              
              <div className="team-info">
                <Users size={14} />
                <span>{team.employee_ids.length} membre(s)</span>
              </div>

              {team.leader_employee_id && (
                <div className="team-leader">
                  <strong>Chef d'équipe:</strong> {getEmployeeName(team.leader_employee_id)}
                </div>
              )}

              {team.service_capabilities.length > 0 && (
                <div className="team-capabilities">
                  {team.service_capabilities.slice(0, 3).map((cap, idx) => (
                    <span key={idx} className="capability-tag">{cap}</span>
                  ))}
                  {team.service_capabilities.length > 3 && (
                    <span className="capability-more">+{team.service_capabilities.length - 3}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
            <h3>{editingTeam ? 'Modifier l\'équipe' : 'Nouvelle équipe'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Nom de l'équipe *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Code *</label>
                  <input
                    type="text"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder="TEAM-01"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Chef d'équipe</label>
                <select
                  value={formData.leader_employee_id}
                  onChange={(e) => setFormData({ ...formData, leader_employee_id: e.target.value })}
                >
                  <option value="">Aucun</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Zone de service</label>
                <input
                  type="text"
                  value={formData.home_zone}
                  onChange={(e) => setFormData({ ...formData, home_zone: e.target.value })}
                  placeholder="Bromont, Granby..."
                />
              </div>

              <div className="form-group">
                <label>Membres de l'équipe</label>
                <div className="employees-checkboxes">
                  {employees.map(emp => (
                    <label key={emp.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.employee_ids.includes(emp.id)}
                        onChange={() => handleToggleEmployee(emp.id)}
                      />
                      <span>{emp.full_name} ({emp.role})</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Compétences</label>
                <div className="capabilities-input">
                  <input
                    type="text"
                    value={newCapability}
                    onChange={(e) => setNewCapability(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCapability())}
                    placeholder="Ajouter une compétence..."
                  />
                  <button type="button" onClick={handleAddCapability} className="btn-add-small">
                    <Plus size={16} />
                  </button>
                </div>
                <div className="capabilities-list">
                  {formData.service_capabilities.map((cap, idx) => (
                    <span key={idx} className="capability-tag">
                      {cap}
                      <button type="button" onClick={() => handleRemoveCapability(cap)}>&times;</button>
                    </span>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                />
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  {editingTeam ? 'Mettre à jour' : 'Créer'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Teams;